import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password reset — you can now sign in");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Could not reset password");
    } finally {
      setBusy(false);
    }
  };

  const field = "mt-1 bg-white";

  return (
    <div className="max-w-md mx-auto px-4 py-24">
      <div className="card-metal p-8" data-testid="reset-password-card">
        {done ? (
          <div className="text-center flex flex-col items-center gap-4" data-testid="reset-done">
            <CheckCircle2 className="w-14 h-14 text-green-600" />
            <h1 className="font-heading font-black text-2xl">Password Reset</h1>
            <p className="text-slate-600">Your password has been updated. You can now sign in with your new password.</p>
            <button onClick={() => navigate("/auth")} className="btn-red-3d" data-testid="go-to-login-btn">Go to Sign In</button>
          </div>
        ) : !token ? (
          <div className="text-center">
            <h1 className="font-heading font-black text-2xl mb-2">Invalid Link</h1>
            <p className="text-slate-600">This reset link is missing or malformed. Please request a new one from the sign-in page.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="flex items-center gap-2">
              <KeyRound className="w-6 h-6 text-[#9b111e]" />
              <h1 className="font-heading font-black text-2xl">Choose a New Password</h1>
            </div>
            <div>
              <Label>New password</Label>
              <Input type="password" required className={field} data-testid="reset-password-input"
                value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters" />
            </div>
            <div>
              <Label>Confirm new password</Label>
              <Input type="password" required className={field} data-testid="reset-password-confirm"
                value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </div>
            <button type="submit" disabled={busy} className="btn-red-3d w-full" data-testid="reset-submit-btn">
              {busy ? "Resetting…" : "Reset Password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
